import { get, has } from 'lodash';
import template from 'url-template';

import awsHeader from '../helpers/awsAuth';
import { enqueueSnackbar, handleNetworkError } from './applicationMessageActions';
import { getSortOrder } from '../helpers/collections';
import k2aBaseApi from '../apis/key2act';

import {
    FETCH_PROFILE_OBJECTS,
    PROFILE_OBJECT_REMOVE,
    PROFILE_OBJECT_REORDER,
    PROFILE_OBJECT_RESET,
    PROFILE_OBJECT_TOGGLE_SELECT,
    PROFILE_OBJECT_UPDATE
} from './actionTypes';

const ProfileObjectsUrl = template.parse('/basprofiles/{profileId}/basprofile-objects{?limit,page,searchFilter,sort}');
const SpecificProfileObjectUrl = template.parse('/basprofile-objects/{objectId}');

const PAGE_LIMIT = 1000;

export const createProfileObject = (profileId, profileObjectData, parentOBIXPath) => async (dispatch) => {
    const header = await awsHeader();

    const profileObjectParams = _getParams(profileObjectData, parentOBIXPath);

    try {
        const response = await k2aBaseApi.post(
            ProfileObjectsUrl.expand({ profileId }),
            profileObjectParams,
            header
        );
        const profileObject = {
            ...profileObjectParams,
            ...response.data
        };

        await dispatch(updateProfileObject(profileObject.id, profileObject));
        await dispatch(fetchProfileObjects(profileId));

        return profileObject;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createBacNetProfileObject = (profileId, body) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            ProfileObjectsUrl.expand({ profileId }),
            body,
            header
        );

        await dispatch(fetchProfileObjects(profileId));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const deleteProfileObject = (profileObject) => async (dispatch) => {
    const { headers } = await awsHeader();
    try {
        await k2aBaseApi.delete(
            SpecificProfileObjectUrl.expand({
                objectId: profileObject.id
            }),
            {
                headers
            }
        );
        dispatch(removeProfileObject(profileObject));
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchProfileObjects = (profileId, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    let totalPayload = [];
    let page = 1;

    try {
        const getPayload = async () => {
            options.page = page;
            options.limit = PAGE_LIMIT;

            const response = await k2aBaseApi.get(
                ProfileObjectsUrl.expand({
                    profileId,
                    ...options
                }),
                header
            );

            const data = get(response, 'data.data', []);
            const totalCount = get(response, 'data.meta.totalCount', 0);

            const payload = data.map((profileObject) => {
                const obixId = profileObject.oBIXPath;
                return {
                    ...profileObject,
                    obixId
                };
            });

            totalPayload = [...totalPayload, ...payload];

            page++;

            if (data.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

        dispatch({
            type:    FETCH_PROFILE_OBJECTS,
            payload: totalPayload
        });
        return totalPayload;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateBacNetProfileObject = (profileId, body) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            SpecificProfileObjectUrl.expand({
                objectId: body.id
            }),
            body,
            header
        );

        await dispatch(fetchProfileObjects(profileId));
        dispatch(enqueueSnackbar('Profile object updated successfully', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const putProfileObject = (profileObject) => async (dispatch) => {
    const header = await awsHeader();
    const profileObjectParams = _getParams(profileObject);

    try {
        await k2aBaseApi.put(
            SpecificProfileObjectUrl.expand({
                objectId: profileObject.id
            }),
            profileObjectParams,
            header
        );

        dispatch(updateProfileObject(profileObject.id, {...profileObject, ...profileObjectParams}));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};


export const removeProfileObject = ({ id }) => ({
    type:    PROFILE_OBJECT_REMOVE,
    payload: {
        id
    }
});

export const resetProfileObjects = () => ({
    type:    PROFILE_OBJECT_RESET,
    payload: {}
});

export const reorderProfileObjects = (newOrderBy) => async (dispatch, getState) => {
    const currentState = getState().profileObjects;
    const order = getSortOrder(newOrderBy, currentState);
    dispatch({
        type:    PROFILE_OBJECT_REORDER,
        payload: {
            order,
            orderBy: newOrderBy
        }
    });
};

export const updateProfileObject = (id, patch) => ({
    type:    PROFILE_OBJECT_UPDATE,
    payload: {
        id,
        patch
    }
});

export const toggleSelectProfileObject = (id) => ({
    type:    PROFILE_OBJECT_TOGGLE_SELECT,
    payload: { id }
});

const _getParams = (profileObject, parentOBIXPath) => {
    if (!profileObject.oBIXPath) return profileObject;

    profileObject.oBIXPath = _calculateRelativePath(profileObject, parentOBIXPath);

    if (!has(profileObject, 'objectType') && has(profileObject, 'type')) profileObject.objectType = profileObject.type;

    return profileObject;
};

const _calculateRelativePath = ({ oBIXPath }, parentPath) => {
    return oBIXPath.replace(parentPath, '');
};
